import { getClassLevel } from './classIdentity.mjs';

// Class-granted Fighting Style (2024 rules, SRD 5.2). Fighter chooses at level 1, Ranger and Paladin at
// level 2; each class saves its own choice at classState.<class>.fightingStyle = { id, changedAtLevel }.
// A choice can be replaced when that class gains a level.

export const FIGHTING_STYLES = Object.freeze([
  { id: 'archery', name: 'Archery', description: '+2 to attack rolls with Ranged weapons.' },
  { id: 'defense', name: 'Defense', description: '+1 AC while wearing armor. A Shield alone does not qualify.' },
  { id: 'great-weapon-fighting', name: 'Great Weapon Fighting', description: 'Treat damage dice showing 1 or 2 as 3 with a qualifying Melee weapon held in two hands.' },
  { id: 'two-weapon-fighting', name: 'Two-Weapon Fighting', description: 'Include your ability modifier in the extra attack granted by Light, including Nick.' },
]);

/** Ranger-only alternative to a Fighting Style feat. */
export const DRUIDIC_WARRIOR = Object.freeze({
  id: 'druidic-warrior',
  name: 'Druidic Warrior',
  description: 'Learn two Druid cantrips that use Wisdom and count as Ranger spells. When you gain a Ranger level, you can replace one of them.',
});
export const DRUIDIC_WARRIOR_CANTRIP_COUNT = 2;

/** Paladin-only alternative to a Fighting Style feat. */
export const BLESSED_WARRIOR = Object.freeze({
  id: 'blessed-warrior',
  name: 'Blessed Warrior',
  description: 'Learn two Cleric cantrips that use Charisma and count as Paladin spells. When you gain a Paladin level, you can replace one of them.',
});

/**
 * Fighting Style options that teach two cantrips from another class's list, saved as
 * fightingStyle.cantrips and cast through the `<grantPrefix><spellId>` feature grant.
 */
export const CANTRIP_STYLES = Object.freeze({
  [DRUIDIC_WARRIOR.id]: Object.freeze({ style: DRUIDIC_WARRIOR, classId: 'ranger', spellList: 'druid', listLabel: 'Druid', ability: 'wisdom', grantPrefix: 'ranger-druidic-warrior-' }),
  [BLESSED_WARRIOR.id]: Object.freeze({ style: BLESSED_WARRIOR, classId: 'paladin', spellList: 'cleric', listLabel: 'Cleric', ability: 'charisma', grantPrefix: 'paladin-blessed-warrior-' }),
});
export const STYLE_CANTRIP_COUNT = DRUIDIC_WARRIOR_CANTRIP_COUNT;
export const getCantripStyle = (styleId) => CANTRIP_STYLES[styleId] || null;
export const cantripStyleForGrant = (grantId) => Object.values(CANTRIP_STYLES).find((entry) => String(grantId || '').startsWith(entry.grantPrefix)) || null;

export const FIGHTING_STYLE_CLASSES = Object.freeze({ fighter: 1, ranger: 2, paladin: 2 });

export const getFightingStyleOptions = (classId) => {
  if (!FIGHTING_STYLE_CLASSES[classId]) return [];
  const cantripStyles = Object.values(CANTRIP_STYLES).filter((entry) => entry.classId === classId).map((entry) => entry.style);
  return [...FIGHTING_STYLES, ...cantripStyles];
};

const classLabel = (classId) => classId.charAt(0).toUpperCase() + classId.slice(1);
export const fightingStyleClassLabel = classLabel;

/** Whether the character has reached the level where this class grants a Fighting Style. */
export const hasFightingStyleFeature = (character, classId) => {
  const required = FIGHTING_STYLE_CLASSES[classId];
  return Boolean(required) && getClassLevel(character, classId) >= required;
};

/** The saved style option for one class, or null when the class has not reached the feature. */
export const getClassFightingStyle = (character, classId) => {
  if (!hasFightingStyleFeature(character, classId)) return null;
  const saved = character?.classState?.[classId]?.fightingStyle;
  const option = getFightingStyleOptions(classId).find((style) => style.id === saved?.id);
  return option ? { ...option, ...(Array.isArray(saved.cantrips) ? { cantrips: [...saved.cantrips] } : {}) } : null;
};

const styleNameKey = (value) => String(value ?? '').trim().toLowerCase().replace(/[’']/g, '').replace(/[\s_-]+/g, ' ');
const STYLE_ID_BY_NAME = new Map(FIGHTING_STYLES.map((style) => [styleNameKey(style.name), style.id]));
/** Fighting Style feats chosen in Feats (2024: the four style feats), by style id. */
export const getFightingStyleFeatIds = (character) => (Array.isArray(character?.feat) ? character.feat : [])
  .map((feat) => STYLE_ID_BY_NAME.get(styleNameKey(feat?.featName ?? feat))).filter(Boolean);

/** Additional Fighting Style (Champion 7): another Fighting Style feat, saved at classState.fighter.additionalFightingStyle
 * = { id, changedAtLevel }. Only the four style feats qualify, and it must differ from the class style. Only a Champion 7
 * can save one (server route; eligibility checks live in champion.mjs, which reads the subclass registry), so a saved
 * choice applies from Fighter level 7. */
export const ADDITIONAL_FIGHTING_STYLE_FEATURE_ID = 'fighter:champion:additional-fighting-style';
export const getAdditionalFightingStyleOptions = (character) =>
  FIGHTING_STYLES.filter((style) => style.id !== getClassFightingStyle(character, 'fighter')?.id);
export const getAdditionalFightingStyle = (character) => {
  if (getClassLevel(character, 'fighter') < 7) return null;
  const saved = character?.classState?.fighter?.additionalFightingStyle;
  return FIGHTING_STYLES.find((style) => style.id === saved?.id) || null;
};

/** True when a class grants this style, the Champion's Additional Fighting Style chose it, or it was taken as a Fighting
 * Style feat; effects apply either way. */
export const hasFightingStyle = (character, id) =>
  Object.keys(FIGHTING_STYLE_CLASSES).some((classId) => getClassFightingStyle(character, classId)?.id === id)
  || getAdditionalFightingStyle(character)?.id === id
  || getFightingStyleFeatIds(character).includes(id);

/** A class's style can be chosen when none is saved, and replaced after gaining a level in that class. */
export const canChangeClassFightingStyle = (character, classId) => {
  if (!hasFightingStyleFeature(character, classId)) return false;
  const saved = getClassFightingStyle(character, classId);
  return !saved || getClassLevel(character, classId) > Number(character.classState[classId].fightingStyle.changedAtLevel);
};

/** Cantrip IDs a class's cantrip style (Druidic Warrior, Blessed Warrior) teaches, in saved order. */
export const getClassStyleCantrips = (character, classId) => {
  const style = getClassFightingStyle(character, classId);
  return getCantripStyle(style?.id)?.classId === classId ? (style.cantrips || []).slice(0, STYLE_CANTRIP_COUNT) : [];
};
export const getDruidicWarriorCantrips = (character) => getClassStyleCantrips(character, 'ranger');

export const styleCantripGrantId = (styleId, spellId) => `${CANTRIP_STYLES[styleId].grantPrefix}${spellId}`;
export const druidicWarriorGrantId = (spellId) => styleCantripGrantId(DRUIDIC_WARRIOR.id, spellId);

/** Every style cantrip the character can cast: [{ classId, styleId, styleName, spellId, grantId, ability }]. */
export const getStyleCantripGrants = (character) => Object.values(CANTRIP_STYLES).flatMap((entry) =>
  getClassStyleCantrips(character, entry.classId).map((spellId) => ({
    classId: entry.classId, styleId: entry.style.id, styleName: entry.style.name, spellId,
    grantId: `${entry.grantPrefix}${spellId}`, ability: entry.ability,
  })));
